import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
};

export default function VendorPricing({ user }) {
    const [pricingList, setPricingList] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vendorFilter, setVendorFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingPricing, setEditingPricing] = useState(null);
    const [toast, setToast] = useState(null);

    // Form state
    const [vendorId, setVendorId] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [basePrice, setBasePrice] = useState('');
    const [perKmPrice, setPerKmPrice] = useState('');
    const [surgePrice, setSurgePrice] = useState('');
    const [status, setStatus] = useState('Active');

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchPricing = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/vendor-pricing/${user?.operatorId || 'all'}`);
            setPricingList(res.data);
        } catch (err) {
            console.error('Fetch pricing error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`/api/vendors/${user?.operatorId || 'all'}`);
            setVendors(res.data);
        } catch (err) {
            console.error('Fetch vendors error:', err);
        }
    };

    useEffect(() => {
        fetchPricing();
        fetchVendors();
    }, [user]);

    const formatPrice = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return 'KWD 0.000';
        return `KWD ${num.toFixed(3)}`;
    };

    const filteredPricing = useMemo(() => {
        if (!vendorFilter) return pricingList;
        return pricingList.filter(p => (p.vendorId?._id || p.vendorId) === vendorFilter);
    }, [pricingList, vendorFilter]);

    const openCreateModal = () => {
        setEditingPricing(null);
        setVendorId('');
        setServiceType('');
        setBasePrice('');
        setPerKmPrice('');
        setSurgePrice('');
        setStatus('Active');
        setShowModal(true);
    };

    const openEditModal = (pricing) => {
        setEditingPricing(pricing);
        setVendorId(pricing.vendorId?._id || pricing.vendorId || '');
        setServiceType(pricing.serviceType || '');
        setBasePrice(pricing.basePrice?.toString() || '');
        setPerKmPrice(pricing.perKmPrice?.toString() || '');
        setSurgePrice(pricing.surgePrice?.toString() || '');
        setStatus(pricing.status || 'Active');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingPricing(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                operatorId: user?.operatorId || user?._id,
                vendorId,
                serviceType,
                basePrice: parseFloat(basePrice) || 0,
                perKmPrice: parseFloat(perKmPrice) || 0,
                surgePrice: parseFloat(surgePrice) || 0,
                status,
            };

            if (editingPricing) {
                await axios.put(`/api/vendor-pricing/${editingPricing._id}`, payload);
                showToast('Pricing updated successfully');
            } else {
                await axios.post('/api/vendor-pricing', payload);
                showToast('Pricing created successfully');
            }

            fetchPricing();
            closeModal();
        } catch (err) {
            console.error('Save pricing error:', err);
            showToast(err.response?.data?.message || 'Failed to save pricing', 'error');
        }
    };

    const handleDelete = async (pricing) => {
        if (!window.confirm('Are you sure you want to delete this pricing?')) return;
        try {
            await axios.delete(`/api/vendor-pricing/${pricing._id}`);
            showToast('Pricing deleted successfully');
            fetchPricing();
        } catch (err) {
            showToast('Failed to delete pricing', 'error');
        }
    };

    return (
        <div className="vp-page">
            <style>{`
                .vp-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }

                .vp-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .vp-header h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }

                .vp-new-btn {
                    background: #6366f1; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
                }
                .vp-new-btn:hover { background: #4f46e5; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(99, 102, 241, 0.4); }

                .vendor-filter-bar {
                    display: flex; align-items: center; gap: 14px; margin-bottom: 20px;
                    padding: 14px 20px; background: white; border-radius: 12px;
                    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.03);
                }
                .vendor-filter-bar label {
                    font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap;
                }
                .vendor-filter-select {
                    padding: 9px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    min-width: 240px; cursor: pointer; transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .vendor-filter-select:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .vp-card {
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); overflow: hidden;
                }

                .vp-table { width: 100%; border-collapse: collapse; }
                .vp-table th {
                    padding: 14px 24px; text-align: left; font-size: 12px; font-weight: 600;
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 1px solid #e2e8f0; background: #fff;
                }
                .vp-table td {
                    padding: 16px 24px; border-bottom: 1px solid #f1f5f9; vertical-align: middle;
                    color: #334155;
                }
                .vp-table tbody tr { transition: background 0.15s; }
                .vp-table tbody tr:hover { background: #f8fafc; }
                .vp-table tbody tr:last-child td { border-bottom: none; }

                .vp-vendor-name { font-weight: 600; color: #1e293b; }

                .vp-price { font-weight: 600; color: #6366f1; }

                .vp-status-badge {
                    display: inline-block; padding: 4px 14px; border-radius: 99px;
                    font-size: 12px; font-weight: 600;
                }
                .vp-status-active { background: #dcfce7; color: #166534; }
                .vp-status-under-review { background: #fef9c3; color: #92400e; }
                .vp-status-inactive { background: #fee2e2; color: #991b1b; }

                .vp-actions { display: flex; gap: 8px; }
                .vp-act-btn {
                    border: none; background: none; padding: 7px; cursor: pointer; border-radius: 6px;
                    transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .vp-act-edit { color: #6366f1; }
                .vp-act-edit:hover { background: #eef2ff; color: #4f46e5; }
                .vp-act-delete { color: #ef4444; }
                .vp-act-delete:hover { background: #fef2f2; color: #dc2626; }

                /* Modal */
                .vp-overlay {
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000;
                    animation: vpFadeIn 0.2s ease;
                }
                @keyframes vpFadeIn { from { opacity: 0; } to { opacity: 1; } }

                .vp-modal {
                    background: white; border-radius: 16px; width: 100%; max-width: 480px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    animation: vpSlideUp 0.25s ease;
                }
                @keyframes vpSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                .vp-modal-header {
                    padding: 20px 24px; border-bottom: 1px solid #f1f5f9;
                    display: flex; justify-content: space-between; align-items: center;
                }
                .vp-modal-header h2 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; }
                .vp-close-btn { background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 6px; display: flex; }
                .vp-close-btn:hover { color: #64748b; background: #f1f5f9; }

                .vp-modal-body { padding: 24px; }

                .vp-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 18px; }
                .vp-form-label { font-size: 13px; font-weight: 600; color: #475569; }
                .vp-form-input {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px;
                    outline: none; transition: all 0.2s; background: #fff;
                    font-family: 'Inter', sans-serif; color: #1e293b;
                }
                .vp-form-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
                .vp-form-input::placeholder { color: #94a3b8; }

                .vp-modal-footer {
                    padding: 18px 24px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 12px;
                }
                .vp-cancel-btn {
                    padding: 10px 20px; border: 1.5px solid #e2e8f0; background: white; border-radius: 8px;
                    font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s;
                }
                .vp-cancel-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
                .vp-submit-btn {
                    padding: 10px 24px; border: none; background: #6366f1; color: white;
                    border-radius: 8px; font-weight: 600; cursor: pointer;
                    transition: all 0.2s; box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
                }
                .vp-submit-btn:hover { background: #4f46e5; transform: translateY(-1px); }

                .vp-empty { text-align: center; padding: 60px 20px; color: #94a3b8; font-size: 15px; }

                .vp-toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    color: white; z-index: 2000; font-weight: 500; font-size: 14px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.15); animation: vpSlideUp 0.3s ease;
                }
                .vp-toast.success { background: #10b981; }
                .vp-toast.error { background: #ef4444; }
            `}</style>

            {toast && <div className={`vp-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="vp-header">
                <h1>Vendor Service Price List</h1>
                <button className="vp-new-btn" onClick={openCreateModal}>
                    <Icon d={icons.plus} size={18} /> Add Pricing
                </button>
            </div>

            <div className="vendor-filter-bar">
                <label>Filter by Vendor:</label>
                <select
                    className="vendor-filter-select"
                    value={vendorFilter}
                    onChange={e => setVendorFilter(e.target.value)}
                >
                    <option value="">All Vendors</option>
                    {vendors.map(v => (
                        <option key={v._id} value={v._id}>{v.vendorName}</option>
                    ))}
                </select>
            </div>

            <div className="vp-card">
                <table className="vp-table">
                    <thead>
                        <tr>
                            <th>Vendor</th>
                            <th>Service</th>
                            <th>Base Price</th>
                            <th>Per KM</th>
                            <th>Surge</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" className="vp-empty">Loading pricing...</td></tr>
                        ) : filteredPricing.length === 0 ? (
                            <tr><td colSpan="7" className="vp-empty">No pricing found. Click "Add Pricing" to add one.</td></tr>
                        ) : (
                            filteredPricing.map(p => (
                                <tr key={p._id}>
                                    <td><span className="vp-vendor-name">{p.vendorId?.vendorName || 'Deleted Vendor'}</span></td>
                                    <td>{p.serviceType}</td>
                                    <td><span className="vp-price">{formatPrice(p.basePrice)}</span></td>
                                    <td><span className="vp-price">{formatPrice(p.perKmPrice)}</span></td>
                                    <td><span className="vp-price">{formatPrice(p.surgePrice)}</span></td>
                                    <td>
                                        <span className={`vp-status-badge vp-status-${p.status?.toLowerCase().replace(' ', '-')}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="vp-actions">
                                            <button className="vp-act-btn vp-act-edit" title="Edit" onClick={() => openEditModal(p)}>
                                                <Icon d={icons.edit} size={16} />
                                            </button>
                                            <button className="vp-act-btn vp-act-delete" title="Delete" onClick={() => handleDelete(p)}>
                                                <Icon d={icons.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="vp-overlay" onClick={closeModal}>
                    <div className="vp-modal" onClick={e => e.stopPropagation()}>
                        <div className="vp-modal-header">
                            <h2>{editingPricing ? 'Edit Pricing Details' : 'Add Pricing Details'}</h2>
                            <button className="vp-close-btn" onClick={closeModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="vp-modal-body">
                                <div className="vp-form-group">
                                    <label className="vp-form-label">Vendor</label>
                                    <select
                                        className="vp-form-input"
                                        required
                                        value={vendorId}
                                        onChange={e => setVendorId(e.target.value)}
                                    >
                                        <option value="">Select a vendor</option>
                                        {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                    </select>
                                </div>
                                <div className="vp-form-group">
                                    <label className="vp-form-label">Service Type</label>
                                    <input
                                        type="text"
                                        className="vp-form-input"
                                        required
                                        placeholder="Standard, Premium, etc."
                                        value={serviceType}
                                        onChange={e => setServiceType(e.target.value)}
                                    />
                                </div>
                                <div className="vp-form-group">
                                    <label className="vp-form-label">Base Price</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        className="vp-form-input"
                                        value={basePrice}
                                        onChange={e => setBasePrice(e.target.value)}
                                    />
                                </div>
                                <div className="vp-form-group">
                                    <label className="vp-form-label">Per KM Price</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        className="vp-form-input"
                                        value={perKmPrice}
                                        onChange={e => setPerKmPrice(e.target.value)}
                                    />
                                </div>
                                <div className="vp-form-group">
                                    <label className="vp-form-label">Surge Price</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        className="vp-form-input"
                                        value={surgePrice}
                                        onChange={e => setSurgePrice(e.target.value)}
                                    />
                                </div>
                                <div className="vp-form-group">
                                    <label className="vp-form-label">Status</label>
                                    <select
                                        className="vp-form-input"
                                        value={status}
                                        onChange={e => setStatus(e.target.value)}
                                    >
                                        <option value="Under Review">Under Review</option>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className="vp-modal-footer">
                                <button type="button" className="vp-cancel-btn" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="vp-submit-btn">
                                    {editingPricing ? 'Update Pricing' : 'Add Pricing'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
